/**
 * =============================================================================
 * auto-add-product.js
 * =============================================================================
 * Automatically add a product based on rules
 * =============================================================================
 */

window.GHAutoAddProduct = window.GHAutoAddProduct || {
  // Rules for auto-adding products
  rules: [],

  // Dynamically-callable comparator functions
  comparatorFunctions: {
    gte: (a, b) => a >= b,
    gt: (a, b) => a > b,
    lte: (a, b) => a <= b,
    lt: (a, b) => a < b,
    eq: (a, b) => a == b,
  },

  report: [],

  /**
   * Entry point
   */
  init: function () {
    this.rules.forEach((rule) => {
      this.initializeRule(rule);
    });
  },

  /**
   * Add a new auto-add product rule. This MAY be called before init (e.g. from
   * a script tag in the page using PreInitCallbacks).
   *
   * @param {Object} rule
   */
  addRule: function (rule) {
    this.rules.push(rule);
    this.initializeRule(rule);
  },

  /**
   * Setup event handlers for each trigger event within the rule. Only ever
   * initialize a given rule once.
   *
   * @param {Object} rule
   */
  initializeRule: function (rule) {
    if (!rule.initialized) {
      console.log("[A-A-P] Initializing rule / setting up handlers", rule);
      rule.triggerEvents.forEach((triggerEvent) => {
        document.addEventListener(triggerEvent, (e) => this.handleTriggerEvent(e, rule));
      });
    }
    rule.initialized = true;
  },

  /**
   * Handle a trigger event for a rule, checking whether the rule has been met.
   *
   * @param {Event} e The triggered event object
   * @param {Object} rule The corresponding rule
   */
  handleTriggerEvent: async function (e, rule) {
    console.log("[A-A-P]", "Rule triggered!", rule);
    console.log("[A-A-P]", "Event data", e);

    // Reset the report for this new event. Not ideal, as multiple events might
    // get fired simultaneously and this will be overwritten, but it's a start.
    this.report = [];

    // Flag that'll be set to false if any condition fails
    let meetsConditions = true;

    // Check each group of conditions in term (e.g. 'cart' conditions...)
    for (const [conditionGroup, conditions] of Object.entries(rule.conditions)) {
      let meetsConditionGroup = this[
        `check${conditionGroup.charAt(0).toUpperCase() + conditionGroup.slice(1)}Conditions`
      ](conditions, e.detail, rule);
      meetsConditions = meetsConditionGroup && meetsConditions;
    }

    console.log("[A-A-P]", "Met conditions?", meetsConditions);
    console.log("[A-A-P]", "Condition Match Report");
    console.table(this.report);

    // Pass all checks? Add the item to the cart
    if (meetsConditions) {
      console.log("[A-A-P]", "Attempting to add variant", rule.variant);
      let response = await window.MP.Extensions.Cart.add([rule.variant]);
      console.log("[A-A-P]", "Add response", response);
    } else {
      console.warn("[A-A-P]", "Product add rejected as didnt meet conditions");
    }
  },

  /**
   * Check if the rule's cart conditions are met.
   *
   * @param {Object} conditions
   * @param {Object} eventDetail
   * @param {Object} rule
   *
   * @returns {boolean} Whether the conditions are met
   */
  checkCartConditions: function (conditions, eventDetail, rule) {
    let meetsCartConditions = true;

    for (const [conditionCategory, condition] of Object.entries(conditions)) {
      switch (conditionCategory) {
        case "attributes":
          // Check attributes condition against cart
          condition.forEach((attributeCondition) => {
            let result =
              eventDetail.response.attributes[attributeCondition.key].toString() ==
              attributeCondition.value.toString();
            if (!result) {
              meetsCartConditions = false;
            }

            this.report.push({
              step: "cart",
              condition: conditionCategory,
              result: result ? "✅" : "❌",
              target: `${attributeCondition.key}=${attributeCondition.value}`,
              actual: `${attributeCondition.key}=${
                eventDetail.response.attributes[attributeCondition.key]
              }`,
            });
          });
          break;
        case "totalExcludingGiftCards":
          // Check totalExcludingGiftCards condition against cart
          let totalExcludingGiftCards = this.getTotalExcludingGiftCards(eventDetail.cart);
          let result = this.comparatorFunctions[condition.comparator](
            totalExcludingGiftCards,
            condition.value
          );
          if (!result) {
            meetsCartConditions = false;
          }

          this.report.push({
            step: "cart",
            condition: conditionCategory,
            result: result ? "✅" : "❌",
            target: condition.value,
            actual: totalExcludingGiftCards,
            comparator: condition.comparator,
          });
          break;
        case "excludeSelf":
          // Check excludeSelf condition against cart
          // If condition is 'excludeSelf: false', we DON'T want to apply this condition
          if (!condition) {
            continue;
          }

          // Check if our serialized variant is already in the cart by
          // serializing each item in the cart and comparing
          let serializedVariant = JSON.stringify(rule.variant);
          let alreadyHasVariant = eventDetail.cart.some((item) => {
            let serializedExistingLineItem = JSON.stringify({
              id: item.id,
              quantity: item.quantity,
              properties: item.properties,
            });
            return serializedExistingLineItem === serializedVariant;
          });

          if (alreadyHasVariant) {
            meetsCartConditions = false;
          }

          this.report.push({
            step: "cart",
            condition: conditionCategory,
            result: !alreadyHasVariant ? "✅" : "❌",
            target: "Not in cart",
            actual: alreadyHasVariant ? "Already in cart" : "Not in cart",
          });
          break;
        case "conditionalProductsIds":
          // Check conditionalProductsIds availabe in cart
          // If condition is 'conditionalProductsIds: [], not availabe', we DON'T want to apply this condition
          if (!condition) {
            continue;
          }

          // Check if our serialized variant is already in the cart by
          // serializing each item in the cart and comparing
          let conditionalProductsInCart = eventDetail.cart.some((item) => {
            return condition.includes(item.product_id);
          });

          // Check variant in cart
          // Check if our serialized variant is already in the cart by
          // serializing each item in the cart and comparing
          let serializedVariantRule = JSON.stringify(rule.variant);
          let alreadyHasVariantInCart = eventDetail.cart.some((item) => {
            let serializedExistingLineItem = JSON.stringify({
              id: item.id,
              quantity: item.quantity,
              properties: item.properties,
            });
            return serializedExistingLineItem === serializedVariantRule;
          });

          if (!conditionalProductsInCart) {
            meetsCartConditions = false;

            // Remove variant
            if (alreadyHasVariantInCart && rule.removeVariant) {
              window.MP.Extensions.Cart.change(rule.removeVariant);
            }
          }

          this.report.push({
            step: "cart",
            condition: "conditionalProductsIds",
            result: conditionalProductsInCart ? "✅" : "❌",
            target: "Already in cart",
            actual: conditionalProductsInCart ? "Already in cart" : "Not in cart",
          });
          break;
        default:
          console.warn("[A-A-P] Unknown condition category", conditionCategory);
      }
    }

    return meetsCartConditions;
  },

  /**
   * Get the monetary total of all items in the cart, excluding gift cards
   *
   * @param {Array} items The cart items
   * @returns {Integer} The total of all items in the cart, excluding gift cards
   */
  getTotalExcludingGiftCards: function (items) {
    return items.reduce((previousValue, item) => {
      if (item.gift_card === true) {
        return previousValue;
      } else {
        return previousValue + item.final_line_price;
      }
    }, 0);
  },
};
